import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';

import i18n from '@/i18n';

import { ApiClientError } from './api-client';

const localizedRootMessage = (error: unknown): string => {
  const fallback = i18n.t('efs.issues.errors.UNKNOWN_ERROR', { ns: 'issues' });
  if (!(error instanceof ApiClientError)) return fallback;
  const key = `efs.issues.errors.${error.code}`;
  return i18n.exists(key, { ns: 'issues' }) ? i18n.t(key, { ns: 'issues' }) : fallback;
};

const detailMessage = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    for (const item of value) {
      const message = detailMessage(item);
      if (message) return message;
    }
    return null;
  }
  if (value && typeof value === 'object' && 'message' in value) {
    const message = (value as { message?: unknown }).message;
    return typeof message === 'string' ? message : null;
  }
  return null;
};

export const applyApiFieldErrors = <TValues extends FieldValues>(
  form: UseFormReturn<TValues>,
  error: unknown,
  fields: readonly Path<TValues>[],
): void => {
  let handled = false;

  if (error instanceof ApiClientError) {
    for (const field of fields) {
      const message = detailMessage(error.details?.[field]);
      if (message) {
        form.setError(field, { type: error.code, message });
        handled = true;
      }
    }

    if (!handled) {
      form.setError('root', { type: error.code, message: localizedRootMessage(error) });
    }
    return;
  }

  form.setError('root', {
    type: 'unknown',
    message: localizedRootMessage(error),
  });
};
